import { useState, useEffect } from 'react'
import { Modal, Button, Form, Spinner, Alert, Table, Badge, Dropdown } from 'react-bootstrap'
import {
  topographSearchCompanies,
  topographGetDocuments,
  topographOrder,
  fetchPointsLeft,
} from '../../service/topograph'
import { TopographCompany, TopographDocument } from '../../types/topograph.type'
import { t } from '../../locale'
import { CountryFlag } from '../CountryFlag'

const COUNTRIES: Record<string, string> = {
  default: '--',
  DE: 'Deutschland',
  CH: 'Schweiz',
  AT: 'Österreich',
  BE: 'Belgien',
  BA: 'Bosnien und Herzegowina',
  BG: 'Bulgarien',
  HR: 'Kroatien',
  CY: 'Zypern',
  CZ: 'Tschechien',
  DK: 'Dänemark',
  EE: 'Estland',
  FI: 'Finnland',
  FR: 'Frankreich',
  GR: 'Griechenland',
  GG: 'Guernsey',
  HU: 'Ungarn',
  IS: 'Island',
  IE: 'Irland',
  IM: 'Isle of Man',
  IT: 'Italien',
  JE: 'Jersey',
  LV: 'Lettland',
  LI: 'Liechtenstein',
  LU: 'Luxemburg',
  MT: 'Malta',
  MD: 'Moldau',
  MC: 'Monaco',
  NL: 'Niederlande',
  NO: 'Norwegen',
  PL: 'Polen',
  PT: 'Portugal',
  RO: 'Rumänien',
  RU: 'Russland',
  RS: 'Serbien',
  SK: 'Slowakei',
  SI: 'Slowenien',
  ES: 'Spanien',
  SE: 'Schweden',
  UA: 'Ukraine',
  GB: 'Vereinigtes Königreich',
  'US-AL': 'Alabama (US)',
  'US-AK': 'Alaska (US)',
  'US-AZ': 'Arizona (US)',
  'US-AR': 'Arkansas (US)',
  'US-CA': 'Kalifornien (US)',
  'US-CO': 'Colorado (US)',
  'US-CT': 'Connecticut (US)',
  'US-DE': 'Delaware (US)',
  'US-FL': 'Florida (US)',
  'US-GA': 'Georgia (US)',
  'US-HI': 'Hawaii (US)',
  'US-ID': 'Idaho (US)',
  'US-IL': 'Illinois (US)',
  'US-IN': 'Indiana (US)',
  'US-IA': 'Iowa (US)',
  'US-KS': 'Kansas (US)',
  'US-KY': 'Kentucky (US)',
  'US-LA': 'Louisiana (US)',
  'US-ME': 'Maine (US)',
  'US-MD': 'Maryland (US)',
  'US-MA': 'Massachusetts (US)',
  'US-MI': 'Michigan (US)',
  'US-MN': 'Minnesota (US)',
  'US-MS': 'Mississippi (US)',
  'US-MO': 'Missouri (US)',
  'US-MT': 'Montana (US)',
  'US-NE': 'Nebraska (US)',
  'US-NV': 'Nevada (US)',
  'US-NH': 'New Hampshire (US)',
  'US-NJ': 'New Jersey (US)',
  'US-NM': 'New Mexico (US)',
  'US-NY': 'New York (US)',
  'US-NC': 'North Carolina (US)',
  'US-ND': 'North Dakota (US)',
  'US-OH': 'Ohio (US)',
  'US-OK': 'Oklahoma (US)',
  'US-OR': 'Oregon (US)',
  'US-PA': 'Pennsylvania (US)',
  'US-RI': 'Rhode Island (US)',
  'US-SC': 'South Carolina (US)',
  'US-SD': 'South Dakota (US)',
  'US-TN': 'Tennessee (US)',
  'US-TX': 'Texas (US)',
  'US-UT': 'Utah (US)',
  'US-VT': 'Vermont (US)',
  'US-VA': 'Virginia (US)',
  'US-WA': 'Washington (US)',
  'US-DC': 'Washington DC (US)',
  'US-WV': 'West Virginia (US)',
  'US-WI': 'Wisconsin (US)',
  'US-WY': 'Wyoming (US)',
  AG: 'Antigua und Barbuda',
  VG: 'Britische Jungferninseln',
  KY: 'Kaimaninseln',
  CN: 'China',
  HK: 'Hongkong',
  MH: 'Marshallinseln',
  MU: 'Mauritius',
  SG: 'Singapur',
  TEST: 'TEST',
}

const DATAPOINT_LABELS: Record<string, string> = {
  company: 'Unternehmensdaten',
  legalRepresentatives: 'Gesetzliche Vertreter',
  otherKeyPersons: 'Weitere Schlüsselpersonen',
  establishments: 'Niederlassungen',
  shareholders: 'Gesellschafter',
  subsidiaries: 'Tochtergesellschaften',
  ultimateBeneficialOwners: 'Wirtschaftlich Berechtigte',
  companyRelationships: 'Unternehmensbeziehungen',
  graph: 'Eigentümerstrukturdiagramm',
}

const SINGLE_DOC_FIELDS = [
  'tradeRegisterExtract',
  'certifiedTradeRegisterExtract',
  'tradeRegisterHistory',
  'ultimateBeneficialOwnersCertificate',
  'certificateOfGoodStanding',
  'lastFiscalYearFinancialStatement',
]

// DE: requires per-customer Transparenzregister credentials
// FR: personal fields redacted without INPI RBE credentials
// HR: UBO data not available through Sudreg
// CN: UBO data not available through the Credit China portal
const EXCLUDE_UBO_DOC_COUNTRIES = ['DE', 'FR', 'HR', 'CN']
const ARRAY_DOC_FIELDS = [
  'financialStatements',
  'articlesOfAssociation',
  'officialPublications',
  'annualReturns',
  'otherDocuments',
]

const DOC_LABELS: Record<string, string> = {
  tradeRegisterExtract:
    'Aktueller Handelsregisterauszug des Unternehmens aus dem amtlichen Register.',
  certifiedTradeRegisterExtract: 'Beglaubigter Handelsregisterauszug mit amtlicher Beglaubigung.',
  tradeRegisterHistory:
    'Chronologischer Handelsregister-Abdruck mit allen Änderungen seit der Gründung.',
  ultimateBeneficialOwnersCertificate:
    'Auszug aus dem Transparenzregister mit den wirtschaftlich Berechtigten.',
  certificateOfGoodStanding: 'Unbedenklichkeitsbescheinigung (Certificate of Good Standing).',
  lastFiscalYearFinancialStatement: 'Jahresabschluss des letzten Geschäftsjahres.',
  financialStatements: 'Eingereichte Jahresabschlussunterlagen des Unternehmens.',
  articlesOfAssociation: 'Gesellschaftsvertrag bzw. Satzung des Unternehmens.',
  officialPublications: 'Amtliche Veröffentlichungen und Bekanntmachungen des Unternehmens.',
  annualReturns: 'Jährliche Rückmeldungen und Jahresberichte (Annual Returns).',
  otherDocuments: 'Weitere amtliche Dokumente und Unterlagen zum Unternehmen.',
}

function formatCents(cents: number): string {
  return `${(cents / 100).toFixed(2)}`
}

interface Props {
  show: boolean
  clientName?: string
  clientId?: string
  onClose: () => void
}

type Step = 'search' | 'select-company' | 'documents' | 'success'

export default function TopographModal({ show, clientName, clientId, onClose }: Props) {
  const [step, setStep] = useState<Step>('search')
  const [country, setCountry] = useState('default')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [companies, setCompanies] = useState<TopographCompany[]>([])
  const [selectedCompany, setSelectedCompany] = useState<TopographCompany | null>(null)
  const [docsData, setDocsData] = useState<any>(null)
  const [selectedDatapoints, setSelectedDatapoints] = useState<string[]>([])
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])
  const [graphBudget, setGraphBudget] = useState('')
  const [showGraphModal, setShowGraphModal] = useState(false)
  const [pendingGraphBudget, setPendingGraphBudget] = useState('')
  const [ordering, setOrdering] = useState(false)
  const [pointsLeft, setPointsLeft] = useState<number>(Infinity)

  function handleClose() {
    setStep('search')
    setQuery('')
    setCompanies([])
    setSelectedCompany(null)
    setDocsData(null)
    setSelectedDatapoints([])
    setSelectedDocIds([])
    setGraphBudget('')
    setPendingGraphBudget('')
    setShowGraphModal(false)
    setError('')
    setPointsLeft(Infinity)
    onClose()
  }

  useEffect(() => {
    if (step === 'documents') {
      fetchPointsLeft().then(setPointsLeft)
    }
    if (clientName) {
      setQuery(clientName)
    }
  }, [step])

  useEffect(() => {
    if (show && clientName) {
      setQuery(clientName)
    }
  }, [show, clientName])

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    if (!query || query.length < 2) return
    setLoading(true)
    setError('')
    try {
      const data = await topographSearchCompanies(country, query)
      const list: TopographCompany[] = Array.isArray(data) ? data : []
      setCompanies(list)
      setStep('select-company')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleSelectCompany(company: TopographCompany) {
    setSelectedCompany(company)
    setLoading(true)
    setError('')
    try {
      const data = await topographGetDocuments(country, company.id)
      setDocsData(data)
      // setSelectedDatapoints(['company'])
      setStep('documents')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  function toggleDatapoint(dp: string) {
    if (dp === 'graph') {
      if (selectedDatapoints.includes('graph')) {
        setSelectedDatapoints(prev => prev.filter(x => x !== 'graph'))
        setGraphBudget('')
      } else {
        setPendingGraphBudget(graphBudget)
        setShowGraphModal(true)
      }
      return
    }
    setSelectedDatapoints(prev => (prev.includes(dp) ? prev.filter(x => x !== dp) : [...prev, dp]))
  }

  function confirmGraphBudget() {
    const budget = parseFloat(pendingGraphBudget.replace(',', '.'))
    setGraphBudget(isNaN(budget) || budget <= 0 ? '' : String(budget))
    setSelectedDatapoints(prev => [...prev, 'graph'])
    setShowGraphModal(false)
  }

  function cancelGraphModal() {
    setShowGraphModal(false)
  }

  function toggleDoc(id: string) {
    setSelectedDocIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  function computeTotal(): number {
    let total = 0
    const prices = docsData?.datapointPrices ?? {}
    for (const dp of selectedDatapoints) {
      total += prices[dp] ?? 0
    }
    const docs = docsData?.documents ?? {}
    const allDocs: TopographDocument[] = []
    for (const f of SINGLE_DOC_FIELDS) {
      if (docs[f]) allDocs.push(docs[f])
    }
    for (const f of ARRAY_DOC_FIELDS) {
      if (Array.isArray(docs[f])) allDocs.push(...docs[f])
    }
    for (const doc of allDocs) {
      if (selectedDocIds.includes(doc.id)) total += doc.price ?? 0
    }
    if (selectedDatapoints.includes('graph') && graphBudget) {
      total += Math.round(parseFloat(graphBudget) * 100)
    }
    return total
  }

  function buildPrices(): Record<string, number> {
    const prices: Record<string, number> = {}
    const dpPrices = docsData?.datapointPrices ?? {}
    for (const dp of selectedDatapoints) prices[dp] = dpPrices[dp] ?? 0
    const docs = docsData?.documents ?? {}
    const allDocs: TopographDocument[] = []
    for (const f of SINGLE_DOC_FIELDS) {
      if (docs[f]) allDocs.push(docs[f])
    }
    for (const f of ARRAY_DOC_FIELDS) {
      if (Array.isArray(docs[f])) allDocs.push(...docs[f])
    }
    for (const doc of allDocs) {
      if (selectedDocIds.includes(doc.id)) prices[doc.id] = doc.price ?? 0
    }
    return prices
  }

  async function handleOrder() {
    setOrdering(true)
    setError('')
    try {
      await topographOrder({
        countryCode: country,
        companyId: selectedCompany!.id,
        dataPoints: selectedDatapoints,
        documents: selectedDocIds,
        prices: buildPrices(),
        graphMaxBudget: selectedDatapoints.includes('graph') ? graphBudget : undefined,
        clientId,
      })
      setStep('success')
    } catch (e: any) {
      if (e.message === 'notEnoughCredit') {
        setError('notEnoughCredit')
      } else {
        setError(e.message)
      }
    } finally {
      setOrdering(false)
    }
  }

  const datapointPrices: Record<string, number> = docsData?.datapointPrices ?? {}
  const availableDatapoints = Object.keys(datapointPrices)

  const totalPoints = computeTotal() / 100
  const insufficientBalance = pointsLeft !== Infinity && totalPoints > pointsLeft

  const allFlatDocs: (TopographDocument & { field: string })[] = []
  if (docsData?.documents) {
    const docs = docsData.documents
    const skipUboDoc = EXCLUDE_UBO_DOC_COUNTRIES.includes(country)
    for (const f of SINGLE_DOC_FIELDS) {
      if (docs[f] && !(skipUboDoc && f === 'ultimateBeneficialOwnersCertificate')) {
        allFlatDocs.push({ ...docs[f], field: f })
      }
    }
    for (const f of ARRAY_DOC_FIELDS) {
      if (Array.isArray(docs[f]))
        docs[f].forEach((d: TopographDocument) => allFlatDocs.push({ ...d, field: f }))
    }
  }

  return (
    <Modal show={show} onHide={handleClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>
          <b>{'KYB - ' + t('Unternehmensdaten')}</b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant='danger'>{error}</Alert>}

        {step === 'search' && (
          <Form onSubmit={handleSearch}>
            <Form.Group className='mb-3'>
              <Form.Label>{t('country')}</Form.Label>
              <Dropdown>
                <Dropdown.Toggle
                  variant='outline-secondary'
                  className='w-100 d-flex align-items-center form-control text-start'
                >
                  <CountryFlag code={country} />
                  <span className='ms-2'>{COUNTRIES[country]}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {Object.entries(COUNTRIES).map(([code, name]) => (
                    <Dropdown.Item
                      key={code}
                      active={code === country}
                      onClick={() => setCountry(code)}
                      className='d-flex align-items-center'
                    >
                      <CountryFlag code={code} />
                      <span className='ms-2'>{name}</span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>{t('firmennameOderNummer')}</Form.Label>
              <Form.Control
                type='text'
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('mind2ZeichenEingeben')}
                minLength={2}
              />
            </Form.Group>
            <Button type='submit' disabled={loading || query.length < 2}>
              {loading ? <Spinner size='sm' /> : 'Suchen'}
            </Button>
          </Form>
        )}

        {step === 'select-company' && (
          <>
            <div className='d-flex justify-content-between mb-3'>
              <Button variant='outline-secondary' size='sm' onClick={() => setStep('search')}>
                ← Zurück
              </Button>
              <span className='text-muted'>
                {companies.length} Ergebnisse für „{query}"
              </span>
            </div>
            {loading && <Spinner />}
            {companies.length === 0 && !loading && (
              <Alert variant='info'>Keine Ergebnisse gefunden.</Alert>
            )}
            <Table hover>
              <tbody>
                {companies.map(c => {
                  const addressParts = []
                  if (c.address?.street) addressParts.push(c.address.street)
                  if (c.address?.postalCode || c.address?.city) {
                    addressParts.push(
                      `${c.address.postalCode ?? ''} ${c.address.city ?? ''}`.trim()
                    )
                  }
                  const matchType = c.matchReason?.matchType
                  return (
                    <tr
                      key={c.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectCompany(c)}
                    >
                      <td>
                        <div className='d-flex align-items-center gap-2'>
                          <strong>{c.legalName ?? '—'}</strong>
                          {matchType === 'id' && <Badge bg='success'>Exakte Übereinstimmung</Badge>}
                          {matchType === 'name' && <Badge bg='info'>Namensübereinstimmung</Badge>}
                        </div>
                        {addressParts.length > 0 && (
                          <div className='text-muted small'>{addressParts.join(', ')}</div>
                        )}
                        <small className='text-muted'>{c.id}</small>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </>
        )}

        {step === 'documents' && docsData && (
          <>
            <div className='d-flex justify-content-between mb-3'>
              <Button
                variant='outline-secondary'
                size='sm'
                onClick={() => setStep('select-company')}
              >
                ← Zurück
              </Button>
              <strong>{selectedCompany?.legalName ?? selectedCompany?.id}</strong>
            </div>

            {error === 'notEnoughCredit' && (
              <Alert variant='warning'>{t('topographModalWarning')}</Alert>
            )}

            {insufficientBalance && error !== 'notEnoughCredit' && (
              <Alert variant='warning'>{t('topographModalWarning')}</Alert>
            )}

            {loading && <Spinner />}

            {(allFlatDocs.length > 0 || availableDatapoints.length > 0) && (
              <Table size='sm' striped className='mb-3'>
                <tbody>
                  {allFlatDocs.map(doc => (
                    <tr key={doc.id}>
                      <td>
                        <Form.Check
                          type='checkbox'
                          id={`doc-${doc.id}`}
                          label={
                            <span>
                              {doc.name}
                              {(DOC_LABELS[doc.field] || doc.description) && (
                                <small className='text-muted d-block'>
                                  {DOC_LABELS[doc.field] ?? doc.description}
                                </small>
                              )}
                            </span>
                          }
                          checked={selectedDocIds.includes(doc.id)}
                          onChange={() => toggleDoc(doc.id)}
                        />
                      </td>
                      <td className='text-end text-nowrap'>
                        {doc.price != null && doc.price > 0 ? (
                          <p>{formatCents(doc.price)}</p>
                        ) : (
                          <p>Inklusive</p>
                        )}
                      </td>
                    </tr>
                  ))}
                  {availableDatapoints.map(dp => (
                    <tr key={dp}>
                      <td>
                        <Form.Check
                          type='checkbox'
                          id={`dp-${dp}`}
                          label={DATAPOINT_LABELS[dp] ?? dp}
                          checked={selectedDatapoints.includes(dp)}
                          onChange={() => toggleDatapoint(dp)}
                        />
                      </td>
                      <td className='text-end text-nowrap'>
                        {dp === 'graph' ? (
                          graphBudget && (
                            <small className='text-muted'>
                              {t('points')}: {parseFloat(graphBudget).toFixed(2)}
                            </small>
                          )
                        ) : datapointPrices[dp] > 0 ? (
                          <p>{formatCents(datapointPrices[dp])}</p>
                        ) : (
                          <p>Inklusive</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <div className='border-top pt-3'>
              <strong>Gesamt: {formatCents(computeTotal())}</strong>
            </div>
          </>
        )}

        {step === 'success' && (
          <Alert variant='success'>
            <Alert.Heading>{t('topographSucess')}</Alert.Heading>
            <p>{t('topographDocumentEmail')}</p>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'documents' && (
          <Button
            onClick={handleOrder}
            disabled={
              ordering ||
              (selectedDatapoints.length === 0 && selectedDocIds.length === 0) ||
              insufficientBalance
            }
          >
            {ordering ? <Spinner size='sm' /> : `Bestellen (${formatCents(computeTotal())})`}
          </Button>
        )}
        <Button variant='secondary' onClick={handleClose}>
          {step === 'success' ? 'Schliessen' : 'Abbrechen'}
        </Button>
      </Modal.Footer>

      <Modal show={showGraphModal} onHide={cancelGraphModal}>
        <Modal.Header closeButton className='bg-gray-light'>
          <Modal.Title>{t('topographGraphConfigure')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t('points')}</Form.Label>
            <Form.Control
              type='number'
              min='1'
              step='0.01'
              placeholder='z. B. 10,00'
              value={pendingGraphBudget}
              onChange={e => setPendingGraphBudget(e.target.value)}
            />
            <Form.Text className='text-muted'>{t('topographGraphBudgetHelp')}</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={cancelGraphModal}>
            Abbrechen
          </Button>
          <Button onClick={confirmGraphBudget}>{t('topographGraphConfirm')}</Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  )
}
